import type { Cart, CartItem } from '@/lib/types/Cart'
import type { Product } from '@/lib/types/Product'
import type { PurchaseData } from '@/lib/types/PurchaseData'

export type Initiator = 'human' | 'system'

const subscribers: ((
  c: Cart,
  e: string,
  payload: CartItem | null | undefined,
  initiator: Initiator,
) => void)[] = []
const fullProductDataCache: Record<string, Product | null> = {}

let cart: Cart
let _getCartPromise: Promise<Cart> | null = null

export async function getCart(): Promise<Cart> {
  if (cart) return cart
  if (!_getCartPromise) {
    _getCartPromise = _getCart()
  }
  cart = await _getCartPromise
  return cart
}

export async function refreshCart(silent = false) {
  cart = await _getCart()
  if (!silent) await notifySubscribers('refresh', null, 'system')
  return cart
}

async function _getCart(): Promise<Cart> {
  const res = await fetch('/cart.js')
  const cart: Cart = await res.json()
  const fullProductData = await Promise.all(
    cart.items.map((item) => {
      const go = async () => ({
        fullProductData: await getFullProductData(item.handle),
        id: item.id,
      })
      return go()
    }),
  )
  return {
    ...cart,
    fullProductData: fullProductData.reduce(
      (obj, item) => ({ ...obj, [item.id]: item.fullProductData }),
      {} as Record<string, Product>,
    ),
  }
}

export async function remove(key: string) {
  await updateQuantity(key, 0)
}

export async function updateAttributes(
  attributes: Record<string, string>,
  silent = false,
  initiator: Initiator = 'human',
) {
  await fetch('/cart/update.js', {
    body: JSON.stringify({
      attributes,
    }),
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  })
  if (!silent) {
    await notifySubscribers('attributesChange', null, initiator)
  }
}

export async function updateProperties(
  key: string,
  properties: Record<string, unknown>,
  originalQuantity?: number,
  silent = false,
  initiator: Initiator = 'human',
) {
  await fetch('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({
      id: key,
      properties,
      quantity: originalQuantity,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  if (!silent) {
    notifySubscribers(
      'propertiesChange',
      cart.items.find((item) => item.key === key),
      initiator,
    )
  }
}

export async function updateQuantity(
  key: string,
  value: number,
  silent = false,
  initiator: Initiator = 'human',
) {
  await fetch('/cart/update.js', {
    method: 'POST',
    body: JSON.stringify({
      updates: {
        [key]: value,
      },
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  if (!silent) {
    notifySubscribers(
      'quantityChange',
      cart.items.find((item) => item.key === key),
      initiator,
    )
  }
}

export async function switchSellingPlan(
  key: string,
  sellingPlanId: string | null,
  quantity: number,
  silent = false,
  initiator: Initiator = 'human',
) {
  await fetch('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({
      id: key,
      quantity,
      selling_plan: sellingPlanId,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  if (!silent) {
    notifySubscribers(
      'quantityChange',
      cart.items.find((item) => item.key === key),
      initiator,
    )
  }
}

async function getFullProductData(handle: string) {
  if (fullProductDataCache[handle] !== undefined)
    return fullProductDataCache[handle]
  try {
    const res = await fetch(`/products/${handle}?view=json-endpoint`)
    const fullProductData = await res.json()
    fullProductDataCache[handle] = fullProductData
    return fullProductData
  } catch (e) {
    fullProductDataCache[handle] = null
    return null
  }
}

export type PayloadAddToCart = Omit<PurchaseData, 'when'>

export async function add(
  purchaseData: PayloadAddToCart,
  silent = false,
  initiator: Initiator = 'human',
) {
  const attribution = purchaseData.attribution
    ? {
        _source: purchaseData.attribution.source,
        _attribution: purchaseData.attribution.view,
      }
    : {}

  const properties = {
    ...purchaseData.properties,
    ...attribution,
  }

  const productDescriptor = {
    quantity: purchaseData.quantity,
    id: purchaseData.selectedVariantId,
    selling_plan: purchaseData.selectedSellingPlanId,
    properties,
  }

  const addedItemResponse = await fetch('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify(productDescriptor),
    headers: { 'content-type': 'application/json' },
  })

  if (!silent) {
    await notifySubscribers('add', await addedItemResponse.json(), initiator)
  }
}

export async function bulkAdd(
  purchaseDataPayload: PayloadAddToCart[],
  silent = false,
  initiator: Initiator = 'human',
) {
  const payload = {
    items: [] as unknown[],
  }
  for (const purchaseData of purchaseDataPayload) {
    const attribution = purchaseData.attribution
      ? {
          _source: purchaseData.attribution.source,
          _attribution: purchaseData.attribution.view,
        }
      : {}

    const properties = {
      ...purchaseData.properties,
      ...attribution,
    }

    payload.items.push({
      quantity: purchaseData.quantity,
      id: purchaseData.selectedVariantId,
      properties,
      selling_plan: purchaseData.selectedSellingPlanId || undefined,
    })
  }

  const addedItemResponse = await fetch('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'content-type': 'application/json' },
  })

  if (!silent) {
    await notifySubscribers(
      'bulkAdd',
      await addedItemResponse.json(),
      initiator,
    )
  }
}

export async function clear(initiator: Initiator = 'human') {
  await fetch('/cart/clear.js', {
    method: 'POST',
  })
  await notifySubscribers('clear', null, initiator)
}

export async function subscribe(
  cb: (
    c: Cart,
    event: string,
    payload: CartItem | null | undefined,
    initiator: Initiator,
  ) => void,
) {
  await getCart()
  cb(cart, 'init', null, 'system')
  subscribers.push(cb)
}

async function notifySubscribers(
  event: string,
  payload: CartItem | null | undefined,
  initiator: Initiator,
) {
  cart = await _getCart()
  for (const subscriber of subscribers) {
    await subscriber(cart, event, payload, initiator)
  }
}

export async function checkout() {
  let mixpanelId: string | undefined
  if (window.mixpanel && window.mixpanel.get_distinct_id) {
    mixpanelId = window.mixpanel.get_distinct_id()
  }
  const form = document.createElement('form')
  form.action = '/cart'
  form.method = 'POST'
  const mixpanelInput = document.createElement('input')
  mixpanelInput.type = 'hidden'
  mixpanelInput.name = 'attributes[mixpanel_id]'
  mixpanelInput.value = mixpanelId ? mixpanelId.toString() : ''
  const submitInput = document.createElement('input')
  submitInput.type = 'hidden'
  submitInput.name = 'checkout'
  submitInput.value = '1'
  form.appendChild(mixpanelInput)
  form.appendChild(submitInput)
  document.body.appendChild(form)
  form.submit()
}
